import { Emitter } from "@core/events/emitter";
import type { Vec3 } from "@models/data/entityData";
import type { DuplicationResult, IDuplicationModel } from "./IDuplicationModel";
import type { IProjectModel } from "./IProjectModel";

/** One copied object: a model's entity, or a card. */
type BufferEntry =
  | { readonly kind: "entity"; readonly sceneId: string; readonly entityId: string }
  | { readonly kind: "card"; readonly sceneId: string; readonly tileId: string };

/**
 * `DuplicationModel` — see {@link IDuplicationModel}.
 *
 * Unity reference: `Models/ProjectModel/DuplicationModel.cs`.
 *
 * Deviation from Unity: the buffer holds `(sceneId, entityId)` pairs rather
 * than live object handles, so a copied entity that is deleted before the
 * paste is skipped instead of being filtered out of `CanPaste` up front — the
 * web has no destroyed-handle sentinel to test. `canPaste` therefore re-checks
 * the ids against the model on every read, which is also what keeps the menu
 * item correct after an undo.
 *
 * CARDS ride the same buffer, as upstream's `GameObject[]` holds them beside
 * models: `DuplicateGameObjects` sends a card to `ProjectModel.DuplicateMediaTile`
 * (`DuplicationModel.cs:100-122`) and everything else to the entity path.
 * Copy / Paste Position works on a card too — its position is the card's own,
 * in the same scene frame as a root model's (`DuplicationModel.cs:206-226`).
 */
export class DuplicationModel implements IDuplicationModel {
  readonly onChanged = new Emitter<void>();

  private readonly m_project: IProjectModel;
  /** Unity `m_buffer` + `m_bufferScene`. */
  private m_buffer: ReadonlyArray<BufferEntry> = [];
  /** Unity `m_copiedPosition`. */
  private m_copiedPosition: Vec3 | null = null;

  constructor(project: IProjectModel) {
    this.m_project = project;
    // Unity drops the buffer with the model. The web keeps the ids across scene
    // switches (paste targets the ACTIVE scene, so the buffer outliving its
    // source scene is the point) but not across projects: an entity id from
    // another project can never resolve.
    project.onActiveProjectChanged.on(() => this.clear());
  }

  get canPaste(): boolean {
    return this.liveBuffer().length > 0;
  }

  get canPastePosition(): boolean {
    return this.m_copiedPosition !== null;
  }

  canCopyOrDuplicate(sceneId: string, snapshotIds: ReadonlyArray<string>): boolean {
    if (snapshotIds.length === 0) return false;
    return snapshotIds.every((snapshotId) => {
      const snapshot = this.m_project.getEntitySnapshotById(sceneId, snapshotId);
      if (snapshot) return snapshot.isRoot;
      // A card is always a root (`MediaAssetFactory` tags it `HierarchyRootTag`).
      return this.m_project.getMediaTile(sceneId, snapshotId) !== null;
    });
  }

  copyEntities(sceneId: string, snapshotIds: ReadonlyArray<string>): void {
    if (!this.canCopyOrDuplicate(sceneId, snapshotIds)) return;
    const buffer = this.toBufferEntries(sceneId, snapshotIds);
    this.releaseBufferScene();
    this.m_buffer = buffer;
    // Unity `CopyGameObjects` pins the buffer's scene (`SetScenePersistent`)
    // because the paste reads the SOURCE rows: without the pin, switching scenes
    // and pasting would resolve nothing. `RemoveBuffer` un-pins it again.
    if (buffer.length > 0) this.m_project.setScenePersistent(sceneId, true);
    this.onChanged.emit();
  }

  async pasteEntitiesAsync(signal?: AbortSignal): Promise<DuplicationResult> {
    const targetSceneId = this.m_project.activeScene?.id;
    if (!targetSceneId) return { entityIds: [], tileIds: [] };
    return this.duplicateEntries(this.liveBuffer(), targetSceneId, signal);
  }

  async duplicateEntitiesAsync(
    sceneId: string,
    snapshotIds: ReadonlyArray<string>,
    signal?: AbortSignal,
  ): Promise<DuplicationResult> {
    if (!this.canCopyOrDuplicate(sceneId, snapshotIds)) return { entityIds: [], tileIds: [] };
    return this.duplicateEntries(this.toBufferEntries(sceneId, snapshotIds), sceneId, signal);
  }

  copyPosition(sceneId: string, snapshotId: string): void {
    const position = this.positionOf(sceneId, snapshotId);
    if (!position) return;
    this.m_copiedPosition = position;
    this.onChanged.emit();
  }

  async pastePositionAsync(
    sceneId: string,
    snapshotId: string,
    signal?: AbortSignal,
  ): Promise<void> {
    const position = this.m_copiedPosition;
    if (!position) return;
    const tile = this.m_project.getMediaTile(sceneId, snapshotId);
    if (tile) {
      if (tile.isLocked) return;
      // Unity's paste is a `Position` write, which the card's undo records.
      await this.m_project.updateMediaTileWithUndoAsync(sceneId, snapshotId, { position }, ["Transform"], {
        label: "Paste position",
      });
      return;
    }
    const snapshot = this.m_project.getEntitySnapshotById(sceneId, snapshotId);
    if (!snapshot || snapshot.isLocked) return;
    await this.m_project.updateEntitySnapshotAsync(
      sceneId,
      snapshotId,
      { transform: { position } },
      signal,
    );
  }

  clear(): void {
    if (this.m_buffer.length === 0 && this.m_copiedPosition === null) return;
    this.releaseBufferScene();
    this.m_buffer = [];
    this.m_copiedPosition = null;
    this.onChanged.emit();
  }

  /** An unlocked object's position — a card's own, or a snapshot's local one. */
  private positionOf(sceneId: string, snapshotId: string): Vec3 | null {
    const tile = this.m_project.getMediaTile(sceneId, snapshotId);
    if (tile) return tile.isLocked ? null : tile.position;
    const snapshot = this.m_project.getEntitySnapshotById(sceneId, snapshotId);
    return snapshot && !snapshot.isLocked ? snapshot.transform.position : null;
  }

  private async duplicateEntries(
    entries: ReadonlyArray<BufferEntry>,
    targetSceneId: string,
    signal?: AbortSignal,
  ): Promise<DuplicationResult> {
    const entityIds: string[] = [];
    const tileIds: string[] = [];
    for (const entry of entries) {
      if (entry.kind === "card") {
        const tile = await this.m_project.duplicateMediaTileAsync(
          entry.sceneId,
          entry.tileId,
          targetSceneId,
          signal,
        );
        if (tile) tileIds.push(tile.id);
        continue;
      }
      const result = await this.m_project.duplicateEntityAsync(
        entry.sceneId,
        entry.entityId,
        signal,
        entry.sceneId === targetSceneId ? undefined : { targetSceneId },
      );
      if (result) entityIds.push(result.entityId);
    }
    return { entityIds, tileIds };
  }

  /** Unity `RemoveBuffer`'s un-pin leg. */
  private releaseBufferScene(): void {
    for (const sceneId of new Set(this.m_buffer.map((entry) => entry.sceneId))) {
      this.m_project.setScenePersistent(sceneId, false);
    }
  }

  /** Buffer entries whose object still exists (Unity's `go != null` filter). */
  private liveBuffer(): ReadonlyArray<BufferEntry> {
    return this.m_buffer.filter((entry) =>
      entry.kind === "card"
        ? this.m_project.getMediaTile(entry.sceneId, entry.tileId) !== null
        : this.m_project.getEntityById(entry.sceneId, entry.entityId) !== null,
    );
  }

  /** Root snapshot ids → their owning entity ids, de-duplicated; card ids as cards. */
  private toBufferEntries(
    sceneId: string,
    snapshotIds: ReadonlyArray<string>,
  ): ReadonlyArray<BufferEntry> {
    const entries: BufferEntry[] = [];
    const seenEntities = new Set<string>();
    for (const snapshotId of snapshotIds) {
      if (this.m_project.getMediaTile(sceneId, snapshotId)) {
        entries.push({ kind: "card", sceneId, tileId: snapshotId });
        continue;
      }
      const entity = this.m_project.getEntityBySnapshotId(sceneId, snapshotId);
      if (entity && !seenEntities.has(entity.id)) {
        seenEntities.add(entity.id);
        entries.push({ kind: "entity", sceneId, entityId: entity.id });
      }
    }
    return entries;
  }
}
